import React from 'react';
import { InstagramCreator } from '@frontend/gql/social/graphql';
import { UserGroupIcon, HandsClappingIcon } from '@revfluence/fresh-icons/regular/esm';
import {
  PostCard,
  PostCardBackground,
  PostCardContent,
  PostCardFooter,
  PostCardAvatar,
  PostCardAvatarImage,
  PostCardAvatarFallback,
  PostCardAvatarName,
  PostCardGradient,
  PostCardHeader,
  PostCardStats,
  PostCardStat,
  PostCardAvatarContainer,
} from '@frontend/applications/Social/components/post-card';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { RecentPostsNoBorderIcon } from '../../../../components/icons/recent-posts-no-border-icon';

interface CreatorPostsCardProps {
  creator: InstagramCreator;
  className?: string;
  isLoading?: boolean;
}

export const CreatorPostsCard: React.FC<CreatorPostsCardProps> = ({
  creator,
  className = '',
  isLoading = false,
}) => {
  const hasValidProfilePicture = creator?.profilePicture && creator.profilePicture !== '';

  if (isLoading) {
    return (
      <PostCard className={`h-[500px] ${className}`}>
        <PostCardContent>
          <PostCardHeader>
            <Skeleton className="w-10 h-10 rounded-full" />
            <div className="flex flex-col justify-between gap-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-32" />
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-16" />
              </div>
            </div>
          </PostCardHeader>
        </PostCardContent>
      </PostCard>
    );
  }

  return (
    <PostCard className={`h-[500px] ${className}`}>
      <PostCardBackground imageUrl={hasValidProfilePicture ? creator.profilePicture : ''} />
      <PostCardGradient />

      {!hasValidProfilePicture && (
        <div className="absolute inset-0 flex items-center justify-center">
          <RecentPostsNoBorderIcon width={120} height={120} className="text-gray-400 opacity-30" />
        </div>
      )}

      <PostCardContent>
        <PostCardFooter className="mt-auto bg-[#E5E8EB] rounded-t-2xl">
          <PostCardAvatarContainer>
            <PostCardAvatar>
              <PostCardAvatarImage
                src={hasValidProfilePicture ? creator.profilePicture : undefined}
                alt={`${creator.username}'s profile picture`}
              />
              <PostCardAvatarFallback name={creator.username}>
                <RecentPostsNoBorderIcon width={30} height={30} />
              </PostCardAvatarFallback>
            </PostCardAvatar>
            <PostCardAvatarName className="text-gray-800">
              {`@${creator.username}`}
            </PostCardAvatarName>
          </PostCardAvatarContainer>

          <div className="text-sm text-gray-700 line-clamp-2 min-h-[2.5rem]">
            {creator?.biography || 'No description available'}
          </div>

          <PostCardStats>
            <PostCardStat
              className="text-gray-800"
              icon={<UserGroupIcon className="h-4 w-4" style={{ color: '#4F7DBA' }} fill="currentColor" />}
              value={creator.insights?.total?.followers || 0}
            />
            <PostCardStat
              className="text-gray-800"
              icon={<HandsClappingIcon className="h-4 w-4" style={{ color: '#EB6192' }} fill="currentColor" />}
              value={creator.insights?.recent?.engagement || 0}
            />
          </PostCardStats>
        </PostCardFooter>
      </PostCardContent>
    </PostCard>
  );
};

export default CreatorPostsCard;
